/**
 * ICON SVG PATH GEOMETRY: the `d` attribute values shared by more than one icon composition, grouped under one export.
 *
 * NAMES DESCRIBE THE GEOMETRY, NEVER THE VALUE. `QUAL-JSX-ATTR-CONST-ADOPTION-001`'s `<ATTR>_<VALUE>` upper-snake
 * convention is meaningful for an ENUMERABLE vocabulary value (`variant="destructive"` -> `VARIANT_DESTRUCTIVE`),
 * where the value IS a word and the name reads as one. It is meaningless for OPAQUE geometry, where the value is a
 * coordinate stream: an identifier transcribed from `'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4'` is longer than the
 * literal it replaces, conveys strictly less, and an import line spelling it tells a reader nothing about which icon
 * it draws. A name that restates its value is not a name.
 *
 * NAMES DESCRIBE THE SHAPE, NOT THE CONSUMER, so a path shared by two icons needs no rename when a third adopts it --
 * `TRAY_OPEN_TOP` is the open-topped tray under both the download and the upload arrow, and it stays that whoever
 * draws it next.
 *
 * MEMBERSHIP IS "DRAWN BY MORE THAN ONE COMPOSITION". A path used by exactly one icon stays inline in that icon: the
 * literal beside its `<path>` is the most readable spelling there is, and hoisting a single-site geometry string here
 * would rebuild a bucket keyed on nothing.
 */

export const ICON_PATH = {
	/** The bell dome and skirt -- the outer body of the notification bell, drawn by `BellIcon` and by the mobile menu's
	 *  notifications entry. Paired with {@link ICON_PATH.BELL_CLAPPER}, which draws the ringer beneath it. */
	BELL_BODY: 'M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9',
	/** The bell clapper -- the small arc hanging below the bell's skirt. Always drawn after {@link ICON_PATH.BELL_BODY}. */
	BELL_CLAPPER: 'M13.73 21a2 2 0 0 1-3.46 0',
	/** The house outline of `HomeIcon`: roof apex down both walls to the rounded base. The door is a separate polyline at
	 *  the call site, not part of this path. */
	HOUSE_OUTLINE: 'M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
	/** The head arc of the SECOND, partially occluded figure in `UsersIcon` -- an open arc rather than the leading
	 *  figure's full circle, because the front head overlaps it. */
	SECOND_PERSON_HEAD_ARC: 'M16 3.13a4 4 0 0 1 0 7.75',
	/** The open-topped tray both `DownloadIcon` and `UploadIcon` draw their arrow into or out of: side walls rising from a
	 *  rounded base, with no lid. The arrow itself is per-icon and stays at the call site. */
	TRAY_OPEN_TOP: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4',
} as const;
